import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  ActiveJob,
  AuditResponse,
  ErrorsResponse,
  EventDetailResponse,
  EventsResponse,
  HistoryResponse,
  JobFilters,
  JobWrite,
  OverviewResponse,
  RunDetail,
  SchedulerJob,
  SchedulerMeta,
  JobWindow,
  SchedulerWindow,
  AckInfo,
} from '@/client/types/scheduler';

const BASE = '/api/admin/v2/scheduler';

const qs = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : '';
};

export const schedulerKeys = {
  all: ['scheduler-v2'] as const,
  overview: (window: string) => [...schedulerKeys.all, 'overview', window] as const,
  events: (params: JobFilters) => [...schedulerKeys.all, 'events', params] as const,
  event: (id: string, window: string) => [...schedulerKeys.all, 'event', id, window] as const,
  active: () => [...schedulerKeys.all, 'active'] as const,
  run: (jobId: string) => [...schedulerKeys.all, 'run', jobId] as const,
  errors: (window: string, status: string) =>
    [...schedulerKeys.all, 'errors', window, status] as const,
  audit: (eventId: string, limit: number) =>
    [...schedulerKeys.all, 'audit', eventId, limit] as const,
  meta: () => [...schedulerKeys.all, 'meta'] as const,
};

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

/** One GET helper so every read has the same unwrap and error handling. */
const useGet = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (path: string): Promise<unknown> =>
      unwrap<unknown>(await request<unknown>(`${BASE}${path}`)),
    'Failed to load the scheduler',
  );
  // withErrorHandling erases generics; restore the typed call.
  return fn as unknown as <T>(path: string) => Promise<T>;
};

const id = encodeURIComponent;

export const useSchedulerOverviewQuery = (window: JobWindow, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.overview(window),
    queryFn: () => get<OverviewResponse>(`/overview${qs({ window })}`),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSchedulerEventsQuery = (params: JobFilters, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.events(params),
    queryFn: () => get<EventsResponse>(`/events${qs({ ...params })}`),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSchedulerEventQuery = (jobId: string, window: JobWindow, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.event(jobId, window),
    queryFn: () => get<EventDetailResponse>(`/events/${id(jobId)}${qs({ window })}`),
    enabled: enabled && !!jobId,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

/** Fetches one more page of run history; the caller appends it. */
export const useSchedulerHistoryFetcher = () => {
  const get = useGet();
  return (jobId: string, offset: number, limit = 50) =>
    get<HistoryResponse>(`/events/${id(jobId)}/history${qs({ offset, limit })}`);
};

export const useSchedulerActiveQuery = (enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.active(),
    queryFn: () => get<{ jobs: ActiveJob[] }>('/jobs/active'),
    enabled,
    refetchInterval: 10_000,
    staleTime: 5_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSchedulerRunQuery = (jobId: string, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.run(jobId),
    queryFn: () => get<RunDetail>(`/jobs/${id(jobId)}`),
    enabled: enabled && !!jobId,
    // A run in progress keeps writing its log.
    refetchInterval: (query) => (query.state.data && !query.state.data.complete ? 5_000 : false),
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSchedulerErrorsQuery = (
  window: SchedulerWindow,
  status: 'open' | 'acknowledged' | 'all',
  enabled = true,
) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.errors(window, status),
    queryFn: () => get<ErrorsResponse>(`/errors${qs({ window, status })}`),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSchedulerAuditQuery = (eventId = '', limit = 100, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.audit(eventId, limit),
    queryFn: () => get<AuditResponse>(`/audit${qs({ event_id: eventId, limit })}`),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useSchedulerMetaQuery = (enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: schedulerKeys.meta(),
    queryFn: () => get<SchedulerMeta>('/meta'),
    enabled,
    staleTime: 1000 * 60 * 10,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Writes: one helper, then a thin hook per action that invalidates every scheduler read. */
const useWrite = <TVars, TRes>(
  build: (vars: TVars) => {
    path: string;
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body: unknown;
  },
  message: string,
) => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(async (vars: TVars): Promise<TRes> => {
    const { path, method, body } = build(vars);
    return unwrap<TRes>(await request<unknown>(`${BASE}${path}`, method, body));
  }, message);
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: schedulerKeys.all }),
  });
};

export const useRunNowMutation = () =>
  useWrite<{ id: string; reason?: string }, { event_id: string; job_ids: string[] }>(
    (v) => ({ path: `/events/${id(v.id)}/run`, method: 'POST', body: { reason: v.reason } }),
    'Failed to start the job',
  );

export const useSetJobEnabledMutation = () =>
  useWrite<
    { id: string; enabled: boolean; reason?: string },
    { event_id: string; enabled: boolean }
  >(
    (v) => ({
      path: `/events/${id(v.id)}/enabled`,
      method: 'PUT',
      body: { enabled: v.enabled, reason: v.reason },
    }),
    'Failed to change the job state',
  );

export const useAbortRunMutation = () =>
  useWrite<{ jobId: string; reason?: string }, { job_id: string; aborted: boolean }>(
    (v) => ({ path: `/jobs/${id(v.jobId)}/abort`, method: 'POST', body: { reason: v.reason } }),
    'Failed to abort the run',
  );

export const useAckErrorsMutation = () =>
  useWrite<
    { eventId: string; note?: string; eventTitle?: string },
    { event_id: string; ack: AckInfo }
  >(
    (v) => ({
      path: `/errors/${id(v.eventId)}/ack`,
      method: 'POST',
      body: { note: v.note, event_title: v.eventTitle },
    }),
    'Failed to acknowledge failures',
  );

export const useCreateJobMutation = () =>
  useWrite<JobWrite, { event: SchedulerJob }>(
    (v) => ({ path: '/events', method: 'POST', body: v }),
    'Failed to create the job',
  );

export const useUpdateJobMutation = () =>
  useWrite<{ id: string; body: JobWrite }, { event: SchedulerJob; changed: string[] }>(
    (v) => ({ path: `/events/${id(v.id)}`, method: 'PATCH', body: v.body }),
    'Failed to save the job',
  );

export const useDeleteJobMutation = () =>
  useWrite<{ id: string; reason: string }, { event_id: string; deleted: boolean }>(
    (v) => ({ path: `/events/${id(v.id)}`, method: 'DELETE', body: { reason: v.reason } }),
    'Failed to delete the job',
  );
